<template>
  <div v-if="production" class="prod-stack">
    <ProductionGaps
      :gaps="production.gaps"
      :production-id="production.id"
      linked
    />
    <section class="prod-summary-grid">
      <router-link
        v-for="tile in tiles"
        :key="tile.to"
        :to="tile.to"
        class="prod-summary-tile"
        :class="{ 'is-missing': tile.missing }"
      >
        <span class="prod-summary-tile__icon">
          <span class="material-icons">{{ tile.icon }}</span>
        </span>
        <span class="min-w-0">
          <p class="prod-summary-tile__label">{{ tile.label }}</p>
          <p class="prod-summary-tile__value">{{ tile.value }}</p>
          <p v-if="tile.hint" class="prod-summary-tile__hint">{{ tile.hint }}</p>
        </span>
      </router-link>
    </section>

    <form class="move-card prod-panel space-y-5" @submit.prevent="onSave">
      <ProductionSectionHead title="Details" subtitle="Rename the production or jump it to a new stage. You can also tap a stage in the header." />
      <div class="move-field !mb-0">
        <label for="detail-name">Production name</label>
        <input id="detail-name" v-model="form.name" required class="move-input" />
      </div>
      <div class="move-field !mb-0">
        <label for="detail-status">Status</label>
        <MoveSelect id="detail-status" v-model="form.status" block :options="productionStatusOptions" />
      </div>
      <p class="text-sm move-text-muted">
        Parent project:
        <span class="font-semibold move-text">{{ production.project?.name }}</span>
      </p>
      <p v-if="message" class="prod-success">{{ message }}</p>
      <button type="submit" class="move-btn-primary" :disabled="workspace.saving.value">Save details</button>
    </form>
  </div>
</template>

<script setup>
import { computed, inject, reactive, ref, watch } from 'vue'
import ProductionSectionHead from '../../components/production/ProductionSectionHead.vue'
import ProductionGaps from '../../components/production/ProductionGaps.vue'
import { editStatusLabels, platformLabels, productionStatusOptions } from '../../config/productionNavigation'
import { formatDateTime } from '../../utils/productionDates'

const workspace = inject('productionWorkspace')
const production = workspace.production
const message = ref('')
const form = reactive({ name: '', status: 'planning' })

const tiles = computed(() => {
  const value = production.value || {}
  const gapKeys = new Set((value.gaps || []).map((gap) => gap.key))
  const people = value.members_count ?? (value.members || []).length
  const platforms = (value.platforms || []).map((key) => platformLabels[key] || key)
  const unassignedPeople = ['talent', 'director', 'presenter', 'crew'].filter((role) => gapKeys.has(role))
  return [
    {
      to: 'people',
      icon: 'people',
      label: 'People',
      value: unassignedPeople.length ? `${unassignedPeople.length} unassigned` : (people ? `${people} assigned` : 'No one yet'),
      hint: unassignedPeople.length ? unassignedPeople.map((role) => role[0].toUpperCase() + role.slice(1)).join(', ') : 'Talent, director, presenter, crew',
      missing: unassignedPeople.length > 0
    },
    {
      to: 'script',
      icon: 'description',
      label: 'Script',
      value: gapKeys.has('script') ? 'Missing' : (value.script_file_name || 'Draft in progress'),
      hint: value.script_file_name ? 'File attached' : 'Open to write or upload',
      missing: gapKeys.has('script')
    },
    {
      to: 'scenes',
      icon: 'movie',
      label: 'Scenes',
      value: gapKeys.has('scenes') ? 'Missing' : `${value.scenes_count} scene${value.scenes_count === 1 ? '' : 's'}`,
      hint: gapKeys.has('mood_board') ? 'Mood board missing' : `${value.mood_board_items_count} mood references`,
      missing: gapKeys.has('scenes') || gapKeys.has('mood_board')
    },
    {
      to: 'shoot',
      icon: 'event',
      label: 'Shoot',
      value: gapKeys.has('shoot') ? 'Day unassigned' : formatDateTime(value.shoot_at),
      hint: gapKeys.has('venue') ? 'Venue missing' : value.shoot_venue,
      missing: gapKeys.has('shoot') || gapKeys.has('venue')
    },
    {
      to: 'platforms',
      icon: 'devices',
      label: 'Platforms',
      value: gapKeys.has('platforms') ? 'Not chosen' : platforms.join(', '),
      hint: Object.keys(value.release_links || {}).length ? 'Live links attached' : 'Where this will publish',
      missing: gapKeys.has('platforms')
    },
    {
      to: 'editing',
      icon: 'content_cut',
      label: 'Editing',
      value: gapKeys.has('editor') ? 'Editor unassigned' : (editStatusLabels[value.edit_status] || 'Not started'),
      hint: value.editor_name || value.editor?.name || 'Assign an editor',
      missing: gapKeys.has('editor')
    }
  ]
})

watch(production, (value) => {
  if (!value) return
  form.name = value.name
  form.status = value.status
}, { immediate: true })

async function onSave () {
  await workspace.save({ name: form.name, status: form.status })
  message.value = 'Saved.'
}
</script>
